import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  Box,
  Button,
  Center,
  Checkbox,
  Heading,
  HStack,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import SignaturePad from './SignaturePad';
import SignedConfirmation from './SignedConfirmation';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function SignEstimate() {
  const { token } = useParams();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [session, setSession] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState('');
  const [numPages, setNumPages] = useState(0);

  const [consent, setConsent] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [selectedOption, setSelectedOption] = useState('');
  const [upgradeOne, setUpgradeOne] = useState(false);
  const [upgradeTwo, setUpgradeTwo] = useState(false);
  const [upgradeThree, setUpgradeThree] = useState(false);
  const [comments, setComments] = useState('');
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [adoptSignature, setAdoptSignature] = useState(false);
  const [adoptError, setAdoptError] = useState(false);
  const [optionError, setOptionError] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);

  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declining, setDeclining] = useState(false);
  const [declined, setDeclined] = useState(false);

  const pdfContainerRef = useRef(null);
  const [pdfWidth, setPdfWidth] = useState(680);

  useEffect(() => {
    // The PDF container doesn't exist in the DOM until loading finishes and
    // the blob URL is ready, so re-run the measurement once those flip
    // rather than only once on mount (when the ref would still be null).
    const updateWidth = () => {
      if (!pdfContainerRef.current) return;
      const available = pdfContainerRef.current.clientWidth - 32;
      setPdfWidth(Math.max(260, Math.min(680, available)));
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [loading, pdfBlobUrl]);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/signing/${token}`
        );
        setSession(response.data);
        setSigned(!!response.data.alreadySigned);
        setDeclined(!!response.data.declined);
        if (response.data.pdf?.data) {
          const blob = new Blob([new Uint8Array(response.data.pdf.data)], {
            type: 'application/pdf',
          });
          setPdfBlobUrl(URL.createObjectURL(blob));
        }
      } catch (error) {
        setLoadError(
          error.response?.data?.message ||
            'This signing link is invalid or has expired.'
        );
      }
      setLoading(false);
    };
    load();
  }, [token]);

  const hasOptions = (session?.options?.length || 0) > 0;

  const formatUSD = amount =>
    amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  const isUpgradeChecked = key =>
    (key === 'upgrade_one' && upgradeOne) ||
    (key === 'upgrade_two' && upgradeTwo) ||
    (key === 'upgrade_three' && upgradeThree);
  const chosenOption = session?.options?.find(
    option => option.value === selectedOption
  );
  const chosenUpgrades = (session?.upgrades || []).filter(upgrade =>
    isUpgradeChecked(upgrade.key)
  );
  // The running total starts once an option is picked (or right away when
  // the estimate only has upgrades) and follows each upgrade toggle.
  const showRunningTotal = hasOptions
    ? !!chosenOption
    : (session?.upgrades?.length || 0) > 0;
  const runningTotal =
    (chosenOption?.price || 0) +
    chosenUpgrades.reduce((sum, upgrade) => sum + (upgrade.price || 0), 0);

  const handlePdfLoaded = ({ numPages }) => {
    setNumPages(numPages);
    // Fires once the PDF has actually rendered, as opposed to the GET above
    // which only means the link was opened -- records estimateViewedDate.
    axios
      .post(`${process.env.REACT_APP_BACKEND_URL}/api/signing/${token}/viewed`)
      .catch(() => {});
  };

  const handleSubmit = async () => {
    if (!signatureDataUrl) {
      toast({
        title: 'Signature required',
        description: 'Please type or draw your signature.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const missingAdopt = !adoptSignature;
    const missingOption = hasOptions && !selectedOption;

    setAdoptError(missingAdopt);
    setOptionError(missingOption);

    if (missingAdopt || missingOption) {
      toast({
        title: 'Please complete the highlighted fields',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/signing/${token}/complete`,
        {
          signatureDataUrl,
          selectedOption,
          upgradeOne,
          upgradeTwo,
          upgradeThree,
          comments,
          signedDateLabel: new Date().toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          }),
        }
      );
      setSession(current => ({ ...current, summary: response.data.summary }));
      setSigned(true);
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error.response?.data?.message ||
          'There was an error submitting your signature.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    setSubmitting(false);
  };

  const handleDecline = async () => {
    setDeclining(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/signing/${token}/decline`,
        { reason: declineReason }
      );
      setDeclined(true);
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error.response?.data?.message ||
          'There was an error declining this estimate.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    setDeclining(false);
  };

  if (loading) {
    return (
      <Center height="100vh">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  if (loadError) {
    return (
      <Center height="100vh" px={4}>
        <VStack spacing={3}>
          <Heading size="md">Unable to load this document</Heading>
          <Text color="gray.600">{loadError}</Text>
        </VStack>
      </Center>
    );
  }

  if (signed) {
    return <SignedConfirmation token={token} session={session} />;
  }

  if (declined) {
    return (
      <Center height="100vh" px={4}>
        <VStack spacing={3}>
          <Heading size="md">You've declined this estimate</Heading>
          <Text color="gray.600">
            The sender has been notified. If this was a mistake, contact them
            directly for a new link.
          </Text>
        </VStack>
      </Center>
    );
  }

  if (showForm) {
    return (
      <Center minH="100vh" bg="gray.50" py={10} px={4}>
        <Box
          w="100%"
          maxW="640px"
          bg="white"
          borderRadius="md"
          boxShadow="md"
          p={{ base: 5, md: 8 }}
        >
          <VStack align="stretch" spacing={5}>
            <Box>
              <Heading size="md">{session.jobName || 'Estimate'}</Heading>
              <Text fontSize="sm" color="gray.600">
                Finish signing below.
              </Text>
            </Box>

            {hasOptions && (
              <Box
                p={optionError ? 3 : 0}
                borderWidth={optionError ? '1px' : 0}
                borderColor="red.400"
                borderRadius="md"
                bg={optionError ? 'red.50' : 'transparent'}
              >
                <Text fontWeight="bold" fontSize="sm" mb={2}>
                  <Text as="span" color="red.500">
                    *
                  </Text>{' '}
                  Select an option (required)
                </Text>
                <RadioGroup
                  value={selectedOption}
                  onChange={value => {
                    setSelectedOption(value);
                    setOptionError(false);
                  }}
                >
                  <Stack spacing={2}>
                    {session.options.map(option => (
                      <Radio key={option.value} value={option.value}>
                        {option.label} — {option.amount}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
                {optionError && (
                  <Text fontSize="xs" color="red.600" mt={2}>
                    Please select an option before signing.
                  </Text>
                )}
              </Box>
            )}

            {session.upgrades?.length > 0 && (
              <Box>
                <Text fontWeight="bold" fontSize="sm" mb={2}>
                  Upgrades (optional)
                </Text>
                <Stack spacing={2}>
                  {session.upgrades.map(upgrade => (
                    <Checkbox
                      key={upgrade.key}
                      isChecked={isUpgradeChecked(upgrade.key)}
                      onChange={e => {
                        const checked = e.target.checked;
                        if (upgrade.key === 'upgrade_one')
                          setUpgradeOne(checked);
                        if (upgrade.key === 'upgrade_two')
                          setUpgradeTwo(checked);
                        if (upgrade.key === 'upgrade_three')
                          setUpgradeThree(checked);
                      }}
                    >
                      {upgrade.label} — {upgrade.amount}
                    </Checkbox>
                  ))}
                </Stack>
              </Box>
            )}

            {(hasOptions || session.upgrades?.length > 0) && (
              <Box
                bg="blue.50"
                borderLeftWidth="4px"
                borderLeftColor="blue.500"
                borderRadius="md"
                px={4}
                py={3}
                aria-live="polite"
              >
                {showRunningTotal ? (
                  <VStack align="stretch" spacing={1}>
                    {chosenOption && (
                      <HStack justify="space-between" fontSize="sm">
                        <Text color="gray.700" noOfLines={1}>
                          {chosenOption.label}
                        </Text>
                        <Text color="gray.700">{chosenOption.amount}</Text>
                      </HStack>
                    )}
                    {chosenUpgrades.map(upgrade => (
                      <HStack
                        key={upgrade.key}
                        justify="space-between"
                        fontSize="sm"
                      >
                        <Text color="gray.700" noOfLines={1}>
                          + {upgrade.label}
                        </Text>
                        <Text color="gray.700">{upgrade.amount}</Text>
                      </HStack>
                    ))}
                    <HStack justify="space-between" pt={1}>
                      <Text fontWeight="bold">Total</Text>
                      <Text fontWeight="bold" fontSize="xl">
                        {formatUSD(runningTotal)}
                      </Text>
                    </HStack>
                  </VStack>
                ) : (
                  <HStack justify="space-between">
                    <Text fontWeight="bold">Total</Text>
                    <Text fontSize="sm" color="gray.600">
                      Select an option to see your total
                    </Text>
                  </HStack>
                )}
              </Box>
            )}

            <Box>
              <Text fontWeight="bold" fontSize="sm" mb={2}>
                Comments (optional)
              </Text>
              <Textarea
                size="sm"
                placeholder="Shingle color or any other relevant info"
                value={comments}
                onChange={e => setComments(e.target.value)}
              />
            </Box>

            <Box>
              <Text fontWeight="bold" fontSize="sm" mb={2}>
                Signature
              </Text>
              <SignaturePad
                signerName={session.customerName}
                onChange={setSignatureDataUrl}
              />
              <Box
                mt={3}
                p={adoptError ? 3 : 0}
                borderWidth={adoptError ? '1px' : 0}
                borderColor="red.400"
                borderRadius="md"
                bg={adoptError ? 'red.50' : 'transparent'}
              >
                <Checkbox
                  isChecked={adoptSignature}
                  onChange={e => {
                    setAdoptSignature(e.target.checked);
                    setAdoptError(false);
                  }}
                >
                  <Text fontSize="sm">
                    <Text as="span" color="red.500">
                      *
                    </Text>{' '}
                    I adopt the signature shown above as my electronic signature
                    on this document.
                  </Text>
                </Checkbox>
                {adoptError && (
                  <Text fontSize="xs" color="red.600" mt={1}>
                    Please confirm you adopt the signature above before signing.
                  </Text>
                )}
              </Box>
            </Box>

            <Stack direction={{ base: 'column', sm: 'row' }} spacing={3}>
              <Button
                colorScheme="green"
                isLoading={submitting}
                onClick={handleSubmit}
                w={{ base: '100%', sm: 'auto' }}
              >
                Complete Signing
              </Button>
              <Button
                variant="ghost"
                onClick={() => setShowForm(false)}
                w={{ base: '100%', sm: 'auto' }}
              >
                Back to document
              </Button>
            </Stack>
          </VStack>
        </Box>
      </Center>
    );
  }

  return (
    <Box minH="100vh" pb={showDecline ? '260px' : '170px'}>
      <Box maxW="900px" mx="auto" px={4} pt={6} pb={2} textAlign="center">
        <Heading size="md">{session.jobName || 'Estimate'}</Heading>
        <Text fontSize="sm" color="gray.600">
          Please review the document below, then continue to sign.
        </Text>
      </Box>

      <Center ref={pdfContainerRef} bg="gray.200" p={4} minH="60vh">
        {pdfBlobUrl && (
          <Document file={pdfBlobUrl} onLoadSuccess={handlePdfLoaded}>
            {Array.from({ length: numPages }, (_, i) => (
              <Box key={i} mb={3} boxShadow="md">
                <Page pageNumber={i + 1} width={pdfWidth} />
              </Box>
            ))}
          </Document>
        )}
      </Center>

      <Box
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        boxShadow="0 -4px 12px rgba(0, 0, 0, 0.08)"
        py={4}
        px={4}
        zIndex={10}
      >
        <Center>
          <VStack spacing={3} maxW="500px" w="100%">
            <Checkbox
              isChecked={consent}
              onChange={e => setConsent(e.target.checked)}
            >
              I have reviewed this document and agree to sign electronically.
            </Checkbox>
            <Button
              colorScheme="blue"
              w="100%"
              isDisabled={!consent}
              onClick={() => setShowForm(true)}
            >
              Continue
            </Button>
            {!showDecline ? (
              <Button
                variant="link"
                colorScheme="red"
                size="sm"
                onClick={() => setShowDecline(true)}
              >
                Decline to sign
              </Button>
            ) : (
              <VStack align="stretch" spacing={2} w="100%">
                <Textarea
                  size="sm"
                  placeholder="Reason (optional)"
                  value={declineReason}
                  onChange={e => setDeclineReason(e.target.value)}
                />
                <HStack>
                  <Button
                    size="sm"
                    colorScheme="red"
                    isLoading={declining}
                    onClick={handleDecline}
                  >
                    Confirm Decline
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowDecline(false)}
                  >
                    Cancel
                  </Button>
                </HStack>
              </VStack>
            )}
          </VStack>
        </Center>
      </Box>
    </Box>
  );
}

export default SignEstimate;
